import {ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';

/**
 * Текстуры анимаций способностей фракций (см. configs/abilityCastAnimation):
 * сердечко и его половинки, кольцо волны, клуб дыма и бомба. Картинок для них
 * в public нет — пиксели считаются кодом и регистрируются через
 * textures.addUint8Array (почему не canvas и почему цвет не умножен на альфу —
 * см. shotProjectileTextures).
 *
 * Все, кроме бомбы и шевронов, БЕЛЫЕ: в игре красятся tint'ом (цвет суккубов,
 * огонь, дым), форма задана только прозрачностью. Бомба и шевроны — в своих
 * цветах: темный чугун с бликом и золото с контуром.
 */

export const ABILITY_CAST_TEXTURE_SIZES = {
  [ABILITY_CAST_TEXTURE_KEYS.HEART]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.HEART_LEFT]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.HEART_RIGHT]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.RING]: {width: 128, height: 128},
  [ABILITY_CAST_TEXTURE_KEYS.PUFF]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.BOMB]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.UPGRADE_CHEVRONS]: {width: 256, height: 256},
  [ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC]: {width: 64, height: 64},
  [ABILITY_CAST_TEXTURE_KEYS.STREAK]: {width: 128, height: 32},
  [ABILITY_CAST_TEXTURE_KEYS.COIN]: {width: 48, height: 48},
  [ABILITY_CAST_TEXTURE_KEYS.SKULL]: {width: 64, height: 64},
};

// Сглаживание края: каждый пиксель проверяется в SUPERSAMPLE × SUPERSAMPLE точках
const SUPERSAMPLE = 3;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const clampByte = (value) => Math.min(255, Math.max(0, Math.round(value)));

const putPixel = (pixels, width, x, y, r, g, b, alpha) => {
  const offset = (y * width + x) * 4;
  pixels[offset] = clampByte(r);
  pixels[offset + 1] = clampByte(g);
  pixels[offset + 2] = clampByte(b);
  pixels[offset + 3] = clampByte(255 * clamp01(alpha));
};

/**
 * Доля пикселя, попавшая внутрь фигуры
 * @param {function(number, number): boolean} isInside — точка в координатах
 *   картинки (пиксели, дробные)
 */
const coverage = (isInside, x, y) => {
  let hits = 0;
  for (let sy = 0; sy < SUPERSAMPLE; sy += 1) {
    for (let sx = 0; sx < SUPERSAMPLE; sx += 1) {
      if (isInside(x + (sx + 0.5) / SUPERSAMPLE, y + (sy + 0.5) / SUPERSAMPLE)) {
        hits += 1;
      }
    }
  }
  return hits / (SUPERSAMPLE * SUPERSAMPLE);
};

const buildWhiteShapePixels = (key, isInside) => {
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = coverage(isInside, x, y);
      if (alpha > 0) {
        putPixel(pixels, width, x, y, 255, 255, 255, alpha);
      }
    }
  }
  return pixels;
};

// Сердце суккубов — демоническое: кривая (x² + y² − 1)³ − x²·y³ ≤ 0 (по x от −1.14
// до 1.14, по y от −1, острие, до 1.25, верх долей) и рожки на долях, до y 1.78.
// Поле картинки чуть шире, чтобы край не упирался в границу текстуры
const HEART_HALF_SPAN = 1.45;
const HEART_CENTER_Y = 0.42;

// Рожок: тонкий, растет прямо из доли и чуть загнут внутрь, без бокового отростка.
// Края снизу вверх, точки (y, |x|): внешний и внутренний сходятся на кончике. Тот же
// рисунок, что у сердец в следах суккубов (scripts/generateUnitFootprints.js)
const HEART_HORN_OUTER = [[0.9, 0.93], [1.2, 0.93], [1.5, 0.87], [1.78, 0.73]];
const HEART_HORN_INNER = [[0.9, 0.65], [1.3, 0.69], [1.55, 0.71], [1.78, 0.73]];

const resolveHornEdgeX = (edge, hy) => {
  for (let index = 1; index < edge.length; index += 1) {
    const [bottomY, bottomX] = edge[index - 1];
    const [topY, topX] = edge[index];
    if (hy <= topY) {
      return bottomX + (topX - bottomX) * ((hy - bottomY) / (topY - bottomY));
    }
  }
  return edge[edge.length - 1][1];
};

const checkIfInsideHeartHorn = (hx, hy) => {
  if (hy < HEART_HORN_OUTER[0][0] || hy > HEART_HORN_OUTER[HEART_HORN_OUTER.length - 1][0]) {
    return false;
  }
  const side = Math.abs(hx);
  return side <= resolveHornEdgeX(HEART_HORN_OUTER, hy) && side >= resolveHornEdgeX(HEART_HORN_INNER, hy);
};

const toHeartSpace = (key, px, py) => {
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  return {
    hx: (px / width * 2 - 1) * HEART_HALF_SPAN,
    // Ось y картинки смотрит вниз, у кривой — вверх
    hy: (1 - py / height * 2) * HEART_HALF_SPAN + HEART_CENTER_Y,
  };
};

const checkIfInsideHeart = (hx, hy) => {
  const base = hx * hx + hy * hy - 1;
  return base * base * base - hx * hx * hy * hy * hy <= 0 || checkIfInsideHeartHorn(hx, hy);
};

// Ломаная трещины сверху вниз: точки (y, x) в координатах кривой сердца. По
// ней раскалываются обе половинки, поэтому они складываются обратно в целое
const HEART_CRACK = [[1.4, 0], [0.95, 0.13], [0.55, -0.12], [0.15, 0.12], [-0.3, -0.1], [-0.7, 0.06], [-1.1, 0]];
// Зазор между половинками по трещине
const HEART_CRACK_GAP = 0.05;

export const resolveHeartCrackX = (hy) => {
  if (hy >= HEART_CRACK[0][0]) {
    return HEART_CRACK[0][1];
  }
  for (let index = 1; index < HEART_CRACK.length; index += 1) {
    const [topY, topX] = HEART_CRACK[index - 1];
    const [bottomY, bottomX] = HEART_CRACK[index];
    if (hy >= bottomY) {
      const share = (topY - hy) / (topY - bottomY);
      return topX + (bottomX - topX) * share;
    }
  }
  return HEART_CRACK[HEART_CRACK.length - 1][1];
};

export const buildHeartPixels = () => buildWhiteShapePixels(ABILITY_CAST_TEXTURE_KEYS.HEART, (px, py) => {
  const {hx, hy} = toHeartSpace(ABILITY_CAST_TEXTURE_KEYS.HEART, px, py);
  return checkIfInsideHeart(hx, hy);
});

export const buildHeartLeftPixels = () => buildWhiteShapePixels(ABILITY_CAST_TEXTURE_KEYS.HEART_LEFT, (px, py) => {
  const {hx, hy} = toHeartSpace(ABILITY_CAST_TEXTURE_KEYS.HEART_LEFT, px, py);
  return checkIfInsideHeart(hx, hy) && hx < resolveHeartCrackX(hy) - HEART_CRACK_GAP / 2;
});

export const buildHeartRightPixels = () => buildWhiteShapePixels(ABILITY_CAST_TEXTURE_KEYS.HEART_RIGHT, (px, py) => {
  const {hx, hy} = toHeartSpace(ABILITY_CAST_TEXTURE_KEYS.HEART_RIGHT, px, py);
  return checkIfInsideHeart(hx, hy) && hx > resolveHeartCrackX(hy) + HEART_CRACK_GAP / 2;
});

/**
 * Кольцо волны: яркий обод с мягкими краями и едва заметная заливка внутри —
 * волна читается и на пестрой подложке
 */
export const buildRingPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.RING;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const center = (width - 1) / 2;
  const radius = width / 2;
  const rimRadius = 0.8;
  const rimWidth = 0.11;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const distance = Math.hypot(x - center, y - center) / radius;
      if (distance >= 1) {
        continue;
      }
      const rim = Math.exp(-(((distance - rimRadius) / rimWidth) ** 2));
      const fill = distance < rimRadius ? 0.1 * distance / rimRadius : 0;
      const alpha = rim + fill;
      if (alpha > 0.004) {
        putPixel(pixels, width, x, y, 255, 255, 255, alpha);
      }
    }
  }
  return pixels;
};

// Клуб дыма — несколько мягких кругов внахлест: центр и радиус в долях
// половины картинки
const PUFF_LOBES = [[0, 0.04, 0.56], [-0.36, 0.14, 0.4], [0.36, 0.16, 0.42], [0.12, -0.3, 0.4], [-0.18, -0.26, 0.36]];

/**
 * Клуб дыма: неровная мягкая клякса, плотнее к середине. Неровность
 * постоянная (не случайная): у каждого клуба своя поворот и размер, этого
 * хватает, чтобы облака не выглядели одинаковыми
 */
export const buildPuffPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.PUFF;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const center = (width - 1) / 2;
  const half = width / 2;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = (x - center) / half;
      const ny = (y - center) / half;
      const density = PUFF_LOBES.reduce((sum, [cx, cy, r]) => {
        const distance = Math.hypot(nx - cx, ny - cy) / r;
        return distance < 1 ? sum + (1 - distance) ** 1.6 : sum;
      }, 0);
      // Легкая рябь, чтобы клуб не был гладким, как свечение
      const ripple = 0.9 + 0.1 * Math.sin(nx * 9.1 + ny * 5.3) * Math.cos(ny * 7.7 - nx * 3.1);
      const alpha = clamp01(density * 1.15) * ripple;
      if (alpha > 0.004) {
        putPixel(pixels, width, x, y, 255, 255, 255, alpha);
      }
    }
  }
  return pixels;
};

// Бомба: центр и радиус корпуса, колпачок и фитиль — в пикселях картинки 64×64
const BOMB = {
  centerX: 29,
  centerY: 37,
  radius: 21,
  // Свет падает слева сверху
  lightX: -0.55,
  lightY: -0.65,
  dark: [22, 22, 30],
  light: [104, 106, 124],
  capCenterX: 44,
  capCenterY: 21,
  capHalfLength: 5.5,
  capHalfWidth: 4,
  capColor: [78, 78, 92],
  fuseColor: [196, 156, 96],
  fuseWidth: 1.6,
};

// Фитиль — дуга от колпачка вверх-вправо (квадратичная кривая)
const BOMB_FUSE = {from: [47, 17], control: [53, 8], to: [59, 9]};

const resolveFuseDistance = (x, y) => {
  let best = Infinity;
  for (let step = 0; step <= 24; step += 1) {
    const t = step / 24;
    const u = 1 - t;
    const fx = u * u * BOMB_FUSE.from[0] + 2 * u * t * BOMB_FUSE.control[0] + t * t * BOMB_FUSE.to[0];
    const fy = u * u * BOMB_FUSE.from[1] + 2 * u * t * BOMB_FUSE.control[1] + t * t * BOMB_FUSE.to[1];
    best = Math.min(best, Math.hypot(x - fx, y - fy));
  }
  return best;
};

/**
 * Бомба с фитилем: темный чугунный шар с бликом, колпачок по диагонали вверх-
 * вправо и короткий фитиль. Где горит фитиль — точка BOMB_FUSE.to (искры и
 * огонек рисует сама анимация, см. GOBLINS_BOMB_CAST.flight.fuseOffset*)
 */
export const buildBombPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.BOMB;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const capAngle = -Math.PI / 4;
  const capCos = Math.cos(capAngle);
  const capSin = Math.sin(capAngle);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const px = x + 0.5;
      const py = y + 0.5;

      // Корпус
      const bodyCoverage = coverage(
        (sx, sy) => Math.hypot(sx - BOMB.centerX, sy - BOMB.centerY) <= BOMB.radius, x, y
      );
      if (bodyCoverage > 0) {
        const nx = (px - BOMB.centerX) / BOMB.radius;
        const ny = (py - BOMB.centerY) / BOMB.radius;
        const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
        const lightLength = Math.hypot(BOMB.lightX, BOMB.lightY, 0.6);
        const diffuse = clamp01((nx * BOMB.lightX + ny * BOMB.lightY + nz * 0.6) / lightLength);
        const specular = Math.pow(clamp01(1 - Math.hypot(nx + 0.42, ny + 0.48) / 0.32), 2);
        const mix = (index) => BOMB.dark[index] + (BOMB.light[index] - BOMB.dark[index]) * diffuse + 230 * specular;
        putPixel(pixels, width, x, y, mix(0), mix(1), mix(2), bodyCoverage);
        continue;
      }

      // Колпачок — прямоугольник, повернутый по диагонали
      const capCoverage = coverage((sx, sy) => {
        const dx = sx - BOMB.capCenterX;
        const dy = sy - BOMB.capCenterY;
        const along = dx * capCos + dy * capSin;
        const across = -dx * capSin + dy * capCos;
        return Math.abs(along) <= BOMB.capHalfWidth && Math.abs(across) <= BOMB.capHalfLength;
      }, x, y);
      if (capCoverage > 0) {
        putPixel(pixels, width, x, y, ...BOMB.capColor, capCoverage);
        continue;
      }

      const fuseDistance = resolveFuseDistance(px, py);
      if (fuseDistance < BOMB.fuseWidth + 1) {
        putPixel(pixels, width, x, y, ...BOMB.fuseColor, clamp01(BOMB.fuseWidth + 1 - fuseDistance));
      }
    }
  }
  return pixels;
};

// Шевроны улучшения: две «галочки» вершиной вверх, как на /img/icons/upgrade.png,
// но толще. Координаты — доли половины картинки от ее центра, y вниз
const CHEVRONS = {
  halfWidth: 0.92,
  // Спад плеча на единицу ширины
  slope: 0.8,
  thickness: 0.5,
  apexes: [-0.97, -0.37],
  // Толщина темного контура и светлой кромки сверху — в тех же долях
  outline: 0.05,
  highlight: 0.08,
  top: [255, 224, 84],
  bottom: [226, 112, 4],
  edge: [255, 248, 190],
  dark: [74, 38, 4],
};

// Со сколькими долями чуть ниже верхней кромки шеврона лежит точка: >0 — внутри
const resolveChevronDistance = (nx, ny, apexY) => {
  const {halfWidth, slope, thickness} = CHEVRONS;
  // Расстояние по вертикали до наклонной кромки переводится в настоящее
  const slantFactor = 1 / Math.hypot(1, slope);
  const topEdge = apexY + slope * Math.abs(nx);
  return {
    fromTop: (ny - topEdge) * slantFactor,
    inside: Math.min((ny - topEdge) * slantFactor, (topEdge + thickness - ny) * slantFactor, halfWidth - Math.abs(nx)),
  };
};

/**
 * Шевроны улучшения: золотой градиент сверху вниз, светлая кромка и темный контур
 * — значок читается и на золотом свечении за ним, и на светлых подложках. Край
 * сглажен по расстоянию до него, без лишних проходов по большой картинке
 */
export const buildUpgradeChevronsPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.UPGRADE_CHEVRONS;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const half = width / 2;
  const pixelSize = 1 / half;
  const {apexes, outline, highlight, top, bottom, edge, dark} = CHEVRONS;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = (x + 0.5 - half) / half;
      const ny = (y + 0.5 - half) / half;
      let best = null;
      apexes.forEach((apexY) => {
        const distance = resolveChevronDistance(nx, ny, apexY);
        if (!best || distance.inside > best.inside) {
          best = distance;
        }
      });
      const alpha = clamp01(best.inside / pixelSize + 0.5);
      if (alpha > 0) {
        const share = clamp01((ny + 1) / 2);
        const body = top.map((channel, index) => channel + (bottom[index] - channel) * share);
        // Кромка сверху плавно переходит в тело, контур — резко
        const glint = clamp01(1 - (best.fromTop - outline) / highlight);
        const lit = body.map((channel, index) => channel + (edge[index] - channel) * glint * 0.85);
        const inner = clamp01((best.inside - outline) / pixelSize + 0.5);
        const color = lit.map((channel, index) => dark[index] + (channel - dark[index]) * inner);
        putPixel(pixels, width, x, y, color[0], color[1], color[2], alpha);
      }
    }
  }
  return pixels;
};

/** Светящийся диск: плотный почти до края, мягко гаснет только у самой кромки */
export const buildSoftDiscPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const center = (width - 1) / 2;
  const radius = width / 2;
  const solidTo = 0.72;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const distance = Math.hypot(x - center, y - center) / radius;
      if (distance < 1) {
        const fall = clamp01((distance - solidTo) / (1 - solidTo));
        putPixel(pixels, width, x, y, 255, 255, 255, (1 - fall) * (1 - fall));
      }
    }
  }
  return pixels;
};

/** Черточка: голова справа, хвост тает влево и сужается */
export const buildStreakPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.STREAK;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const centerY = (height - 1) / 2;
  const headRound = 10;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const along = (x + 0.5) / width;
      const halfWidth = (height / 2) * (0.18 + 0.82 * along ** 1.4);
      const across = Math.abs(y - centerY) / halfWidth;
      if (across < 1) {
        const head = clamp01((width - x) / headRound);
        putPixel(pixels, width, x, y, 255, 255, 255, along ** 1.6 * (1 - across * across) * head);
      }
    }
  }
  return pixels;
};

/** Монета: темный край, светлое поле, вдавленное кольцо и блик сверху слева */
export const buildCoinPixels = () => {
  const key = ABILITY_CAST_TEXTURE_KEYS.COIN;
  const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  const center = (width - 1) / 2;
  const radius = width / 2 - 1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const nx = (x - center) / radius;
      const ny = (y - center) / radius;
      const distance = Math.hypot(nx, ny);
      if (distance <= 1) {
        const edgeAlpha = clamp01((1 - distance) * radius);
        const isRim = distance > 0.8;
        const isGroove = distance > 0.62 && distance <= 0.7;
        const shine = clamp01(0.55 - (nx + ny) * 0.5);
        const base = isRim ? [176, 110, 12] : isGroove ? [196, 130, 18] : [255, 200, 40];
        const mix = (channel, peak) => Math.round(channel + (peak - channel) * shine * (isRim ? 0.3 : 0.55));
        putPixel(pixels, width, x, y, mix(base[0], 255), mix(base[1], 240), mix(base[2], 170), edgeAlpha);
      }
    }
  }
  return pixels;
};

/** Череп: свод с глазницами и носом, ниже — челюсть с прорезями зубов */
export const buildSkullPixels = () => {
  const {width} = ABILITY_CAST_TEXTURE_SIZES[ABILITY_CAST_TEXTURE_KEYS.SKULL];
  const half = width / 2;
  return buildWhiteShapePixels(ABILITY_CAST_TEXTURE_KEYS.SKULL, (px, py) => {
    const x = (px - half) / half;
    const y = (py - half) / half;
    const absX = Math.abs(x);
    const isCranium = Math.hypot(x, (y + 0.16) * 1.08) <= 0.68;
    const isJaw = absX <= 0.36 && y >= 0.22 && y <= 0.76;
    if (!isCranium && !isJaw) {
      return false;
    }
    const isEye = Math.hypot(absX - 0.28, y + 0.1) <= 0.175;
    const isNose = y >= 0.14 && y <= 0.34 && absX <= (y - 0.14) * 0.42;
    const isToothGap = y >= 0.5 && y <= 0.76 && Math.abs(absX - 0.13) <= 0.022;
    return !isEye && !isNose && !isToothGap;
  });
};

const PIXEL_BUILDERS = {
  [ABILITY_CAST_TEXTURE_KEYS.HEART]: buildHeartPixels,
  [ABILITY_CAST_TEXTURE_KEYS.HEART_LEFT]: buildHeartLeftPixels,
  [ABILITY_CAST_TEXTURE_KEYS.HEART_RIGHT]: buildHeartRightPixels,
  [ABILITY_CAST_TEXTURE_KEYS.RING]: buildRingPixels,
  [ABILITY_CAST_TEXTURE_KEYS.PUFF]: buildPuffPixels,
  [ABILITY_CAST_TEXTURE_KEYS.BOMB]: buildBombPixels,
  [ABILITY_CAST_TEXTURE_KEYS.UPGRADE_CHEVRONS]: buildUpgradeChevronsPixels,
  [ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC]: buildSoftDiscPixels,
  [ABILITY_CAST_TEXTURE_KEYS.STREAK]: buildStreakPixels,
  [ABILITY_CAST_TEXTURE_KEYS.COIN]: buildCoinPixels,
  [ABILITY_CAST_TEXTURE_KEYS.SKULL]: buildSkullPixels,
};

/**
 * Зарегистрировать текстуры в сцене (один раз — дальше они лежат в кэше)
 *
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR: сглаживание
 *   при повороте и масштабе
 * @returns {boolean} все текстуры на месте
 */
export const ensureAbilityCastTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(ABILITY_CAST_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = ABILITY_CAST_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
